import React from "react";
import {
  X,
  Building2,
  Mail,
  Phone,
  Globe,
  MapPin,
  Calendar,
  Edit2,
} from "lucide-react";

/**
 * View Organizer Modal Component
 * Displays detailed information about an organizer
 */
const ViewOrganizerModal = ({ isOpen, onClose, organizer, onEdit }) => {
  if (!isOpen || !organizer) return null;


  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center p-4">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col animate-in fade-in-0 zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-6 py-5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="flex overflow-hidden flex-shrink-0 justify-center items-center w-16 h-16 bg-white bg-opacity-20 rounded-xl backdrop-blur">
                {organizer.logo ? (
                  <img
                    src={organizer.logo}
                    alt={organizer.name}
                    className="object-cover w-full h-full"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : null}
                {!organizer.logo && <Building2 className="w-8 h-8 text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-white truncate">
                  {organizer.name}
                </h3>
                <p className="text-sm text-blue-100">Organizer Details</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 p-2 text-white rounded-lg transition-colors hover:text-blue-200 hover:bg-white hover:bg-opacity-20"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Left Column - Quick Info */}
            <div className="space-y-4 lg:col-span-1">
              {/* Slug Card */}
              <div className="p-4 bg-white rounded-xl border border-gray-200">
                <h4 className="mb-2 text-sm font-semibold text-gray-700">
                  Organizer Slug
                </h4>
                <p className="px-3 py-2 font-mono text-sm text-blue-700 break-all bg-blue-50 rounded-lg border border-blue-100">
                  {organizer.slug}
                </p>
              </div>

              {/* Created Date Card */}
              <div className="p-4 bg-white rounded-xl border border-gray-200">
                <h4 className="mb-3 text-sm font-semibold text-gray-700">
                  Account Information
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Created Date</span>
                    <span className="text-sm font-medium text-gray-900">
                      {organizer.created_at
                        ? new Date(organizer.created_at).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        : "N/A"}
                    </span>
                  </div>
                  {organizer.eventsCount !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Events Organized</span>
                      <span className="text-lg font-bold text-blue-600">
                        {organizer.eventsCount || 0}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Detailed Info */}
            <div className="space-y-6 lg:col-span-2">
              {/* Contact Information */}
              <div className="p-5 bg-white rounded-xl border border-gray-200">
                <h4 className="flex items-center mb-4 font-bold text-gray-900">
                  <Phone className="mr-2 w-5 h-5 text-blue-600" />
                  Contact Information
                </h4>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                      Email Address
                    </label>
                    <div className="flex items-center space-x-2">
                      <Mail className="flex-shrink-0 w-4 h-4 text-gray-400" />
                      <a
                        href={`mailto:${organizer.email}`}
                        className="text-sm font-medium text-blue-600 truncate hover:text-blue-700 hover:underline"
                      >
                        {organizer.email}
                      </a>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                      Phone Number
                    </label>
                    <div className="flex items-center space-x-2">
                      <Phone className="flex-shrink-0 w-4 h-4 text-gray-400" />
                      <a
                        href={`tel:${organizer.phone_number}`}
                        className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                      >
                        {organizer.phone_number}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Location Information */}
              <div className="p-5 bg-white rounded-xl border border-gray-200">
                <h4 className="flex items-center mb-4 font-bold text-gray-900">
                  <MapPin className="mr-2 w-5 h-5 text-blue-600" />
                  Location Information
                </h4>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                      Country
                    </label>
                    <div className="flex items-center space-x-2">
                      <Globe className="flex-shrink-0 w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">
                        {organizer.country}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                      City
                    </label>
                    <div className="flex items-center space-x-2">
                      <MapPin className="flex-shrink-0 w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">
                        {organizer.city}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="p-5 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                <h4 className="flex items-center mb-4 font-bold text-gray-900">
                  <Calendar className="mr-2 w-5 h-5 text-blue-600" />
                  Additional Information
                </h4>
                <div className="space-y-3">
                  {organizer.status && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Account Status</span>
                      <span className="text-sm font-medium text-gray-900 capitalize">
                        {organizer.status}
                      </span>
                    </div>
                  )}
                  {organizer.eventsCount !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Total Events</span>
                      <span className="text-sm font-medium text-gray-900">
                        {organizer.eventsCount || 0} events organized
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Created Date</span>
                    <span className="text-sm font-medium text-gray-900">
                      {organizer.created_at
                        ? new Date(organizer.created_at).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })
                        : "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-6 py-4 bg-gray-50 rounded-b-2xl border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors font-medium"
          >
            Close
          </button>
          <button
            onClick={() => {
              onEdit(organizer);
              onClose();
            }}
            className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg transition-all shadow-md hover:shadow-lg font-medium flex items-center"
          >
            <Edit2 className="mr-2 w-4 h-4" />
            Edit Organizer
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewOrganizerModal;

